import type { VoidFunctionComponent } from 'react';

import { IssueScopeKind } from '../../../../shared/issuesV2';
import { LabeledToggle } from '../../generic/Forms/LabeledToggle';
import Dropdown from '../../settings/shared/SettingsPageDropdown';
import type { SelectOption } from '../../../types';

import Section from './SettingsPageSection';

type Props = {
  scopeType: IssueScopeKind.Global | IssueScopeKind.Project;
  sbomAnalysisEnabled: boolean;
  onToggleSBOMAnalysis: (isChecked: boolean) => void;
  policyOptions: Array<SelectOption<number | null>>;
  selectedPolicyOption: SelectOption<number | null>;
  onSelectedPolicyChange: (policyId: number | null) => void;
  canEditPolicy: boolean;
};

/**
 * SettingsPageSBOMSection is a SettingsPageSection.Right component
 * for the SBOM settings section, shared by both project and organization settings.
 */
const SettingsPageSBOMSection: VoidFunctionComponent<Props> = ({
  scopeType,
  sbomAnalysisEnabled,
  onToggleSBOMAnalysis,
  policyOptions,
  selectedPolicyOption,
  onSelectedPolicyChange,
  canEditPolicy,
}) => {
  const scopeProjectsText = scopeType === IssueScopeKind.Global ? 'all new projects' : 'this project';
  return (
    <>
      <LabeledToggle
        label="Scan for SBOM requirements"
        isChecked={sbomAnalysisEnabled}
        onChange={(isChecked) => onToggleSBOMAnalysis(isChecked)}
        isDisabled={!canEditPolicy}
      />
      <Section.Paragraph>This will check for required fields and formats.</Section.Paragraph>
      <Section.Indented isDisabled={!sbomAnalysisEnabled}>
        <Dropdown
          id="settings-project-sbom-policy"
          label="Default SBOM policy"
          options={policyOptions}
          value={selectedPolicyOption}
          onChange={(option: SelectOption<string | number | null> | null) => {
            if (option?.value === null || option?.value) {
              onSelectedPolicyChange(option.value as number | null);
            }
          }}
          isDisabled={!sbomAnalysisEnabled || !canEditPolicy}
        />
        <Section.Paragraph>This policy will be applied by default to {scopeProjectsText}.</Section.Paragraph>
      </Section.Indented>
    </>
  );
};

export default SettingsPageSBOMSection;
